/**
 * ArendaPro+ Worker Client - ТОЛЬКО fetchClient без GoTrueClient
 * 
 * КРИТИЧНО: НЕ создаем Supabase клиент для предотвращения Multiple GoTrueClient instances.
 * Используем только fetchClient для всех DB операций.
 * 
 * @author ArendaPro+ Development Team
 * @version 2.0.0
 * @since 2025-08-16
 */

// ВАЖНО: НЕ импортируем createClient или SupabaseClient!
import { fetchClient } from './fetchClient';
import { supabase as supabaseAuth } from '@/integrations/supabase/client';
import type { Database } from '@/types/supabase';

// ВАЖНО: Экспортируем fetchClient как supabaseWorker для обратной совместимости
export const supabaseWorker = fetchClient;
export const supabaseDirect = fetchClient;

// Конфигурация для Worker прокси (только для диагностики)
const WORKER_PROXY_URL = import.meta.env.VITE_SUPABASE_WORKER_URL || 'https://api.arendapro.plus';
const DIRECT_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://api.arendapro.plus';
const USE_WORKER_PROXY = import.meta.env.VITE_USE_WORKER_PROXY === 'true' || false;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';


/**
 * Утилита для переключения между Worker и прямым подключением
 * @param {boolean} useWorker - Использовать Worker прокси (игнорируется, всегда возвращает fetchClient)
 * @returns {typeof fetchClient} FetchClient для DB операций
 */
export const getSupabaseClient = (useWorker: boolean = USE_WORKER_PROXY) => {
  return fetchClient;
};

/**
 * Проверяет доступность Worker прокси
 * @returns {Promise<boolean>} true если Worker доступен
 */
export const checkWorkerHealth = async (): Promise<boolean> => {
  if (!USE_WORKER_PROXY) {
    return false;
  }

  try {
    const response = await fetch(`${WORKER_PROXY_URL}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        'apikey': supabaseAnonKey
      }
    });
    
    const isHealthy = response.ok;
    console.log('[WorkerClient] Worker health check:', isHealthy ? 'OK' : 'FAILED');
    return isHealthy;
  } catch (error) {
    console.error('[WorkerClient] Worker health check failed:', error);
    return false;
  }
};

/**
 * Экспорт по умолчанию - автоматический выбор клиента
 * В production использует Worker, в development - прямое подключение
 */
export default fetchClient;

// Диагностическая информация
if (import.meta.env.DEV) {
  console.log('[WorkerClient] Configuration:', {
    workerUrl: WORKER_PROXY_URL,
    directUrl: DIRECT_SUPABASE_URL,
    useWorkerProxy: USE_WORKER_PROXY,
    anonKey: supabaseAnonKey ? '***' + supabaseAnonKey.slice(-4) : 'MISSING'
  });
}